<template>
  <v-container fluid>
    <BrAppBar>
      <div class="py-3 title d-none d-sm-block">
        {{ $t('carteira.title') }}
      </div>
    </BrAppBar>

    <div class="title d-sm-none">
      {{ $t('carteira.title') }}
    </div>

    <v-row class="py-3">
      <!-- Left column: balance + summary cards -->
      <v-col class="flex-column" style="display: flex;gap: 25px;" cols="12" md="5">

        <!-- Balance card -->
        <Card :title="$t('carteira.saldoAtual')">
          <v-skeleton-loader
            :loading="loading"
            :transition="'fade-transition'"
            type="heading"
            width="160"
            height="30"
            class="mt-3"
          >
            <div class="saldo">{{ creditBalance | currency }}</div>
          </v-skeleton-loader>

          <div v-if="lastBalanceUpdate" class="last-balance-update mt-1">
            {{ $t('carteira.ultimaAtualizacaoSaldo') }}: {{ formattedLastBalanceUpdate }}
          </div>

          <Button
            variant="primary"
            size="large"
            block
            prepend-icon="mdi-bank-transfer"
            class="mt-5"
            @click="saqueDialog = true"
          >
            {{ $t('carteira.solicitarSaque') }}
          </Button>

          <v-alert
            v-if="minWithdrawAmount > 0"
            dense
            outlined
            type="warning"
            class="mt-3 mb-0"
            style="font-size: 13px;"
          >
            {{ $t('carteira.minWithdraw') }} {{ minWithdrawAmount | currency }}
          </v-alert>
        </Card>

        <!-- Summary cards -->
        <Card :no-pad="true">
          <div class="summary-card" v-for="(item, i) in summaryCards" :key="i" :class="{ 'summary-card--border': i < summaryCards.length - 1 }">
            <v-skeleton-loader :loading="loading" type="text" width="120" height="22">
              <div class="summary-value">{{ item.currency ? currency(item.value) : item.value }}</div>
            </v-skeleton-loader>
            <div class="summary-label">{{ item.label }}</div>
          </div>
        </Card>
      </v-col>

      <!-- Right column: withdraw history -->
      <v-col cols="12" md="7">
        <Card>
          <div class="hist-header">
            <div class="dash-card__header mb-0">
              <span class="dash-card__title">{{ $t('carteira.historicoSaques') }}</span>
              <div class="dash-card__divider"></div>
            </div>
            <div class="d-flex align-center" style="flex-shrink: 0;">
              <span class="mr-2 label-data" style="white-space: nowrap;">{{ $t('common.period') }}:</span>
              <FiltroRangeDate
                @input="filtrarDt"
                :value="rangeDate"
                :labelBtn="$t('common.period')"
                :actionLabels="{ apply: $t('common.ok') }"
                :btnProps="{ outlined: true }"
                style="width: 200px;"
              />
            </div>
          </div>

          <div class="box-table box-table-gecom">
            <v-skeleton-loader :loading="loading" :transition="'fade-transition'" height="400" type="table">
              <v-data-table
                :headers="headersData"
                :items="withdraws"
                :loading="loadingTable"
                :items-per-page.sync="totalItens"
                hide-default-footer
                :mobile-breakpoint="0"
              >
                <template slot="item.value" slot-scope="{ item }">
                  <span class="text-green">{{ item.value | currency }}</span>
                </template>

                <template slot="item.created" slot-scope="{ item }">
                  <span class="text-muted">{{ item.created | datas }}</span>
                </template>

                <template slot="item.status" slot-scope="{ item }">
                  <div class="d-flex justify-start align-center">
                    <div class="point" :class="[formatStatusClass(item.status)]"></div>
                    {{ formatStatus(item.status) }}
                  </div>
                </template>

                <template slot="item.method" slot-scope="{ item }">
                  {{ formatMethod(item) }}
                </template>
              </v-data-table>
            </v-skeleton-loader>
          </div>
        </Card>
      </v-col>
    </v-row>

    <!-- Withdrawal modal -->
    <v-dialog v-model="saqueDialog" max-width="480" :persistent="loadingBtn" scrollable>
      <v-card class="modal-saque">
        <v-card-title class="modal-saque__header">
          <span class="modal-saque__title">{{ $t('carteira.solicitarSaque') }}</span>
          <v-btn icon small @click="saqueDialog = false" :disabled="loadingBtn">
            <v-icon color="#FFFFFF">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="modal-saque__body">
          <div class="mt-4">
            <span class="label">{{ $t('carteira.metodoPagamento') }}</span>
            <v-select
              v-model="typeKeyCode"
              :items="listaTipoChavePix"
              item-text="text"
              item-value="value"
              outlined
              hide-details
              class="select-status mt-1"
            />
          </div>

          <div class="mt-4">
            <span class="label">{{ pixKeyLabel }}</span>
            <v-text-field
              v-if="pixKeyMask"
              :key="'masked-' + typeKeyCode"
              class="mt-1"
              prepend-inner-icon="mdi-key-outline"
              hide-details
              v-model="keyCode"
              :placeholder="pixKeyPlaceholder"
              v-mask="pixKeyMask"
              outlined
            />
            <v-text-field
              v-else
              :key="'free-' + typeKeyCode"
              class="mt-1"
              prepend-inner-icon="mdi-key-outline"
              hide-details
              v-model="keyCode"
              :placeholder="pixKeyPlaceholder"
              outlined
            />
          </div>

          <div class="mt-4" v-if="!hasCnpj">
            <span class="label">{{ $t('carteira.cnpj') }} <span class="text-red">*</span></span>
            <div class="label-sub">{{ $t('carteira.cnpjSub') }}</div>
            <v-text-field
              v-model="cnpj"
              class="mt-1"
              prepend-inner-icon="mdi-card-account-details"
              placeholder="00.000.000/0000-00"
              v-mask="['##.###.###/####-##']"
              hide-details
              outlined
              :rules="[rules.required, rules.cnpj]"
              @input="formatCnpj"
            />
          </div>

          <div class="mt-4">
            <span class="label">{{ $t('carteira.valorSaque') }}</span>
            <v-currency-field
              v-model="value"
              prefix="R$"
              class="mt-1"
              outlined
              hide-details
            />
          </div>

          <!-- Upload da Nota Fiscal -->
          <div class="mt-4">
            <span class="label">{{ $t('carteira.notaFiscal') }} <span class="text-red">*</span></span>
            <div class="label-sub">{{ $t('carteira.notaFiscalSub') }}</div>

            <div
              class="upload-area mt-2"
              @drop.prevent="handleDrop"
              @dragover.prevent
              @dragenter.prevent
              :class="{ 'drag-over': isDragOver }"
              @dragenter="isDragOver = true"
              @dragleave="isDragOver = false"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".pdf"
                @change="handleFileSelect"
                style="display: none"
              />

              <div v-if="!invoiceFile" class="upload-content">
                <v-icon color="#7C7C8A" size="40">mdi-file-pdf-box</v-icon>
                <div class="upload-text">
                  <span class="upload-title">{{ $t('carteira.cliqueSelecionar') }}</span>
                  <span class="upload-subtitle">{{ $t('carteira.ouArraste') }}</span>
                </div>
                <Button variant="outlined" size="small" prepend-icon="mdi-upload" @click="$refs.fileInput.click()">
                  {{ $t('carteira.selecionarPdf') }}
                </Button>
              </div>

              <div v-else class="file-preview">
                <div class="file-info">
                  <v-icon color="primary" size="28">mdi-file-pdf-box</v-icon>
                  <div class="file-details">
                    <span class="file-name">{{ invoiceFile.name }}</span>
                    <span class="file-size">{{ formatFileSize(invoiceFile.size) }}</span>
                  </div>
                </div>
                <v-btn @click="removeFile" icon small color="error" class="remove-btn">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="modal-saque__actions">
          <Button
            variant="primary"
            size="large"
            block
            prepend-icon="mdi-bank-transfer"
            :loading="loadingBtn"
            :disabled="!invoiceFile || !value || value <= 0 || (!hasCnpj && !cnpjCompleto)"
            @click="solicitarSaque()"
          >
            {{ $t('carteira.solicitarSaque') }}
          </Button>
          <Button
            variant="ghost"
            size="default"
            block
            :disabled="loadingBtn"
            class="mt-2"
            @click="saqueDialog = false"
          >
            {{ $t('common.cancel') }}
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import { errorSnackbar, successSnackbar } from '@/core/service/utils'
import { currency } from '@/core/filters/currency'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import { mask } from 'vue-the-mask'

export default {
  name: 'Carteira',
  directives: { mask },
  components: {
    FiltroRangeDate: () => import('@/views/components/filtroRangeDate'),
    BrAppBar: () => import('@/views/layout/appBar'),
    Button: () => import('@/views/components/button'),
    Card: () => import('@/views/components/card'),
  },
  data: () => ({
    loading: false,
    loadingTable: false,
    loadingBtn: false,
    saqueDialog: false,
    value: 0.0,
    typeKeyCode: 'CNPJ',
    keyCode: null,
    cnpj: '',
    invoiceFile: null,
    isDragOver: false,
    rangeDate: {
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD')
    },
    // rules moved to computed for i18n support
  }),
  computed: {
    ...mapGetters('carteira', [
      'withdraws', 'totalItens', 'creditBalance', 'lastBalanceUpdate', 'withdrawBalance',
      'accruedCommissions', 'hasCnpj',
      'minWithdrawAmount', 'subAffiliateCommissions', 'ftdCount',
      'paymentsReceived', 'hasPaymentMethod',
    ]),
    ...mapGetters('auth', ['user']),

    formattedLastBalanceUpdate () {
      const parsed = moment(this.lastBalanceUpdate, ['DD/MM/YYYY HH:mm:ss', moment.ISO_8601], true)
      return parsed.isValid() ? parsed.format('DD/MM/YYYY [às] HH:mm') : this.lastBalanceUpdate
    },

    headersData () {
      return [
        { align: 'start', class: 'table-header', text: this.$t('common.date'),   value: 'created' },
        { align: 'start', class: 'table-header', text: this.$t('common.value'),  value: 'value' },
        { align: 'start', class: 'table-header', text: this.$t('common.status'), value: 'status' },
      ]
    },

    cnpjCompleto () {
      if (!this.cnpj) return false
      return this.cnpj.replace(/\D/g, '').length === 14
    },

    rules () {
      return {
        required: value => !!value || this.$t('common.required'),
        cnpj: value => {
          if (!value) return true
          const apenasNumeros = value.replace(/\D/g, '')
          if (apenasNumeros.length < 14) return this.$t('carteira.cnpjDigits')
          const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/
          return cnpjRegex.test(value) || this.$t('carteira.cnpjFormat')
        },
      }
    },

    summaryCards () {
      return [
        { label: this.$t('carteira.comissoesSubAfiliados'), value: this.subAffiliateCommissions, currency: true },
        { label: this.$t('carteira.quantidadeFtd'),         value: this.ftdCount,                currency: false },
        { label: this.$t('carteira.comissoesAcumuladas'),   value: this.accruedCommissions,      currency: true },
        { label: this.$t('carteira.pagamentosRecebidos'),   value: this.paymentsReceived,        currency: true },
      ]
    },

    listaTipoChavePix () {
      return [
        { value: 'CPF',   text: 'CPF',                              label: this.$t('carteira.pix.chaveCpf'),      placeholder: '000.000.000-00' },
        { value: 'CNPJ',  text: 'CNPJ',                             label: this.$t('carteira.pix.chaveCnpj'),     placeholder: '00.000.000/0000-00' },
        { value: 'PHONE', text: this.$t('carteira.pix.celular'),    label: this.$t('carteira.pix.celular'),       placeholder: '+55 (11) 99999-9999' },
        { value: 'EMAIL', text: 'E-mail',                           label: 'E-mail',                              placeholder: 'seu@email.com' },
        { value: 'OTHER', text: this.$t('carteira.pix.aleatorio'),  label: this.$t('carteira.pix.chaveAleatoria'),placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      ]
    },

    pixKeyLabel () {
      const found = this.listaTipoChavePix.find(it => it.value === this.typeKeyCode)
      return found ? found.label : 'Chave PIX'
    },

    pixKeyPlaceholder () {
      const found = this.listaTipoChavePix.find(it => it.value === this.typeKeyCode)
      return found ? found.placeholder : ''
    },

    pixKeyMask () {
      const masks = {
        CPF:   '###.###.###-##',
        CNPJ:  '##.###.###/####-##',
        PHONE: ['(##) ####-####', '(##) #####-####'],
      }
      return masks[this.typeKeyCode] || null
    },
  },
  watch: {
    typeKeyCode () { this.keyCode = null },
  },
  async mounted () {
    this.buscar()
  },
  methods: {
    ...mapActions('carteira', ['getItens', 'sacar']),
    currency,

    filtrarDt (e) {
      this.rangeDate = { start: e.start, end: e.end }
      this.buscar()
    },

    handleFileSelect (event) {
      this.processFile(event.target.files[0])
    },

    handleDrop (event) {
      this.isDragOver = false
      this.processFile(event.dataTransfer.files[0])
    },

    processFile (file) {
      if (!file) return
      if (file.type !== 'application/pdf') {
        errorSnackbar(this.$t('carteira.soPdf'))
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        errorSnackbar(this.$t('carteira.maxArquivo'))
        return
      }
      this.invoiceFile = file
      successSnackbar(this.$t('carteira.notaFiscalAnexada'))
    },

    removeFile () {
      this.invoiceFile = null
      this.$refs.fileInput.value = ''
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatCnpj (value) {
      let numbers = value.replace(/\D/g, '').substring(0, 14)
      if (numbers.length <= 2) {
        this.cnpj = numbers
      } else if (numbers.length <= 5) {
        this.cnpj = numbers.replace(/(\d{2})(\d{0,3})/, '$1.$2')
      } else if (numbers.length <= 8) {
        this.cnpj = numbers.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3')
      } else if (numbers.length <= 12) {
        this.cnpj = numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4')
      } else {
        this.cnpj = numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5')
      }
    },

    convertFileToBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result.split(',')[1])
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    async solicitarSaque () {
      const { keyCode, typeKeyCode, value, invoiceFile, cnpj } = this

      if (!this.hasCnpj) {
        if (!this.cnpjCompleto) {
          errorSnackbar(this.$t('carteira.cnpjCompleto'))
          return
        }
        const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/
        if (!cnpjRegex.test(cnpj)) {
          errorSnackbar(this.$t('carteira.cnpjFormat'))
          return
        }
      }

      if (!invoiceFile) {
        errorSnackbar(this.$t('carteira.anexeNotaFiscal'))
        return
      }

      if (value > this.creditBalance) {
        errorSnackbar(this.$t('carteira.valorMaiorSaldo'))
        return
      }

      if (!keyCode || keyCode.trim().length === 0) {
        errorSnackbar(`Informe a ${this.pixKeyLabel}!`)
        return
      }

      this.loadingBtn = true
      try {
        const base64File = await this.convertFileToBase64(invoiceFile)
        const requestData = {
          value: parseFloat(value),
          typeKeyCode,
          keyCode: keyCode || null,
          attachment: {
            name: invoiceFile.name,
            size: invoiceFile.size,
            type: invoiceFile.type,
            data: base64File,
          }
        }
        if (!this.hasCnpj && cnpj) requestData.cnpj = cnpj

        await this.sacar(requestData)
        successSnackbar(this.$t('carteira.saqueSucesso'))
        this.saqueDialog = false
        this.clearForm()
        this.buscar()
      } catch (err) {
        errorSnackbar(err.error || this.$t('carteira.erroSaque'))
      } finally {
        this.loadingBtn = false
      }
    },

    clearForm () {
      this.value = 0.0
      this.keyCode = null
      this.cnpj = ''
      this.invoiceFile = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    buscar () {
      this.loading = true
      const { start, end } = this.rangeDate
      this.getItens({ dtStart: start, dtEnd: end })
        .catch(err => errorSnackbar(err.error))
        .finally(() => { this.loading = false })
    },

    formatStatusClass (status) {
      if (status === 'B') return 'Pago'
      if (status === 'C') return 'Cancelado'
      if (status === 'E') return 'Expirado'
      return 'Pendente'
    },

    formatStatus (status) {
      if (status === 'B') return this.$t('carteira.status.pago')
      if (status === 'C') return this.$t('carteira.status.cancelado')
      if (status === 'E') return this.$t('carteira.status.expirado')
      return this.$t('carteira.status.pendente')
    },

    formatMethod (item) {
      const find = this.listaTipoChavePix.find(it => it.value === item.params.pix.typeKeyCode)
      return find ? find.text : item.params.pix.typeKeyCode
    },
  }
}
</script>

<style lang="scss">
@import '~@/assets/styles/components/table';

.title {
  color: #FFF;
}

/* Summary cards */
.summary-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;

  &--border {
    border-bottom: 1px solid rgba(124, 124, 138, 0.2);
  }

  .summary-value {
    color: #FFF;
    font-size: 20px;
    font-weight: 600;
  }

  .summary-label {
    color: #7C7C8A;
    font-size: 14px;
    text-align: right;
    max-width: 55%;
  }
}

/* Upload area */
.upload-area {
  border: 2px dashed #7C7C8A;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  background: rgba(124, 124, 138, 0.05);

  &:hover {
    border-color: var(--v-primary-base);
    background: rgba(41, 195, 83, 0.1);
  }

  &.drag-over {
    border-color: var(--v-primary-base);
    background: rgba(41, 195, 83, 0.1);
    transform: scale(1.02);
  }
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.upload-title {
  color: #FFF;
  font-size: 16px;
  font-weight: 600;
}

.upload-subtitle {
  color: #7C7C8A;
  font-size: 14px;
}


.file-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: rgba(41, 195, 83, 0.1);
  border-radius: 8px;
  border: 1px solid var(--v-primary-base);
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-name {
  color: #FFF;
  font-size: 14px;
  font-weight: 600;
}

.file-size {
  color: #7C7C8A;
  font-size: 12px;
}

.remove-btn {
  background: rgba(239, 41, 41, 0.1) !important;

  &:hover {
    background: rgba(239, 41, 41, 0.2) !important;
  }
}

.label-sub {
  color: #7C7C8A;
  font-size: 14px;
  margin-top: 4px;
}

.text-red {
  color: #EF2929;
}

.point {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  margin-right: 6px;
  flex-shrink: 0;

  &.Pago      { background-color: var(--v-primary-base); box-shadow: 0 0 6px rgba(var(--v-primary-base), 0.5); }
  &.Cancelado { background-color: #EF2929; box-shadow: 0 0 6px rgba(239, 41, 41, 0.4); }
  &.Expirado  { background-color: #EF2929; box-shadow: 0 0 6px rgba(239, 41, 41, 0.4); }
  &.Pendente  { background-color: #EF8229; box-shadow: 0 0 6px rgba(239, 130, 41, 0.4); }
}

.label-data {
  font-size: 16px;
  font-weight: 600;
  color: #FFF;
}


/* Modal */
.modal-saque {
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px !important;
    min-height: unset !important;
  }

  &__title {
    color: #FFF;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  &__body {
    padding: 4px 20px 16px !important;
    overflow-x: hidden;
    overflow-y: auto;
  }

  &__actions {
    flex-direction: column;
    padding: 16px 20px 20px !important;
    gap: 0;
  }
}

.hist-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
  margin-bottom: 16px;
}

.label {
  color: #FFF;
  font-size: 18px;
}

.saldo {
  color: #FFF;
  font-size: 28px;
}

.last-balance-update {
  color: #7C7C8A;
  font-size: 12px;
}

.valor {
  color: #FFF;
  font-size: 20px;
}

/* ── Table typography ── */
.box-table {
  .v-data-table-header-mobile { display: none; }

  ::v-deep .v-data-table {
    background: transparent !important;

    // Header row
    thead tr th {
      background: rgba(255, 255, 255, 0.04) !important;
      color: #7C7C8A !important;
      font-size: 11px !important;
      font-weight: 700 !important;
      letter-spacing: 0.7px !important;
      text-transform: uppercase !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
    }

    .v-data-table-header th.sortable .v-data-table-header__icon {
      color: var(--v-primary-base) !important;
    }

    // Body rows
    tbody tr {
      border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;

      &:hover { background: rgba(255, 255, 255, 0.03) !important; }
      &:last-child td { border-bottom: none !important; }
    }

    tbody td {
      font-size: 13px !important;
      color: #C8C8D4 !important;
      border-bottom: none !important;
      padding-top: 14px !important;
      padding-bottom: 14px !important;
    }
  }
}

.text-muted {
  color: #7C7C8A;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.2px;
}

.text-green {
  color: var(--v-primary-base);
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.2px;
}

.v-text-field--outlined .v-text-field__prefix,
.theme--light.v-select .v-select__selection--comma,
.theme--light.v-input input,
.theme--light.v-input textarea,
.theme--light.v-input ::placeholder {
  color: #FFF !important;
}

/* Scoped: only fix active input labels, not buttons/icons */
.v-application .v-input .v-label.primary--text {
  color: #FFF !important;
}

.v-text-field {
  border-radius: 8px;
}

.v-text-field--outlined {
  input::placeholder { color: #FFF !important; }
  input { color: #FFF !important; }

  fieldset {
    border-radius: 8px;
    border-color: #7C7C8A !important;
    border-width: 1px;
  }

  /* Only prepend/append input icons, not button icons */
  .v-input__prepend-inner i,
  .v-input__append-inner i {
    color: #7C7C8A !important;
  }
}

.theme--light.v-data-table {
  padding-top: 20px !important;
}

.v-data-table>.v-data-table__wrapper>table>thead>tr>th:first-child {
  border-top-left-radius: 8px !important;
  border-bottom-left-radius: 8px !important;
}

.v-data-table>.v-data-table__wrapper>table>thead>tr>th:last-child {
  border-top-right-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
}
</style>
